import { useAuthStore } from "@/stores/auth";
import { useBatchImportStore } from "@/stores/batchImport";
import { useJobsStore } from "@/stores/jobs";
import { WSEvent } from "@shared/constants";
import type { WSEventPayload } from "@shared/types";
import { readonly, ref, type Ref } from "vue";
import { io, type Socket } from "socket.io-client";

class SocketService {
  public socket: Socket | null = null;
  private connectionState = ref(false);
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity;
  private pendingListeners: Array<{
    event: string;
    callback: (...args: unknown[]) => void;
  }> = [];

  connect(): void {
    const authStore = useAuthStore();

    if (!authStore.token) {
      return;
    }

    // App startup and login can both request a connection. Reuse the existing
    // socket so listeners and reconnection state are not lost.
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }

    this.socket = io(import.meta.env.VITE_API_URL || window.location.origin, {
      auth: {
        token: authStore.token,
      },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      this.reconnectAttempts = 0;
      this.connectionState.value = true;
      // Register pending listeners that were added before connection
      if (this.pendingListeners.length > 0) {
        this.pendingListeners.forEach(({ event, callback }) => {
          this.socket?.on(event, callback);
        });
        this.pendingListeners = [];
      }

      // Register job events AFTER connection is established
      this.setupJobEvents();

      // Phase 22.3: Setup batch import listeners after connection
      this.setupBatchImportEvents();
    });

    this.socket.on("disconnect", (reason) => {
      void reason;
      this.connectionState.value = false;
    });

    this.socket.on("connect_error", (error: Error) => {
      console.error("❌ WebSocket connection error:", error.message);
      this.reconnectAttempts++;
      this.connectionState.value = false;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("Max reconnection attempts reached");
        this.disconnect();
      }
    });
  }

  private setupJobEvents(): void {
    if (!this.socket) {
      console.error('❌ [setupJobEvents] Socket is null!');
      return;
    }

    const jobsStore = useJobsStore();

    // Job events
    this.socket.on(
      WSEvent.JOB_COMPLETED,
      async (payload: WSEventPayload[WSEvent.JOB_COMPLETED]) => {
        // Fetch fresh job data to get final rounds/results counts
        await jobsStore.fetchJobById(payload.jobId);
      },
    );

    this.socket.on(
      WSEvent.JOB_CREATED,
      (payload: WSEventPayload[WSEvent.JOB_CREATED]) => {
        void payload;
        void jobsStore.fetchJobs();
      },
    );

    this.socket.on(
      WSEvent.JOB_UPDATED,
      async (payload: WSEventPayload[WSEvent.JOB_UPDATED]) => {
        // Fetch fresh job data to get updated rounds/results counts
        await jobsStore.fetchJobById(payload.jobId);
      },
    );

    this.socket.on(
      WSEvent.JOB_FAILED,
      async (payload: WSEventPayload[WSEvent.JOB_FAILED]) => {
        // Fetch fresh job data to get error details
        await jobsStore.fetchJobById(payload.jobId);
      },
    );

    this.socket.on(
      WSEvent.JOB_DELETED,
      (payload: WSEventPayload[WSEvent.JOB_DELETED]) => {
        jobsStore.removeJob(payload.jobId);
      },
    );

    // Phase 20: Queue status updates
    this.socket.on(
      "queue:updated",
      (payload: WSEventPayload["queue:updated"]) => {
        jobsStore.queueStatus = payload;
      },
    );
  }

  private setupBatchImportEvents(): void {
    if (!this.socket) {
      console.error('❌ [setupBatchImportEvents] Socket is null!');
      return;
    }

    const batchImportStore = useBatchImportStore();
    const authStore = useAuthStore();

    // Join user room for targeted progress events
    if (authStore.user?.userId) {
      this.socket.emit('join', authStore.user.userId);
    }

    // Batch import progress events
    this.socket.on(
      WSEvent.BATCH_IMPORT_PROCESSING_STARTED,
      (payload: WSEventPayload[WSEvent.BATCH_IMPORT_PROCESSING_STARTED]) => {
        void payload;
        batchImportStore.fileUploadProgress = null;
        batchImportStore.processingPending = true;
        batchImportStore.processingStarted = true;
      },
    );

    this.socket.on(
      WSEvent.BATCH_IMPORT_PROGRESS,
      (payload: WSEventPayload[WSEvent.BATCH_IMPORT_PROGRESS]) => {
        batchImportStore.fileUploadProgress = null;
        batchImportStore.processingPending = false;
        batchImportStore.processingStarted = true;
        // Direct assignment triggers Vue reactivity better than $patch for nested objects
        batchImportStore.uploadProgress = {
          current: payload.current,
          total: payload.total,
          percentage: payload.percentage,
          message: payload.message,
        };
      },
    );

    this.socket.on(
      WSEvent.BATCH_IMPORT_COMPLETED,
      (payload: WSEventPayload[WSEvent.BATCH_IMPORT_COMPLETED]) => {
        void payload;
        // Clear progress and reset isUploading after a delay
        setTimeout(() => {
          batchImportStore.uploadProgress = null;
          batchImportStore.fileUploadProgress = null;
          batchImportStore.processingPending = false;
          batchImportStore.processingStarted = false;
          batchImportStore.isUploading = false;
        }, 1000);
      },
    );

    this.socket.on(
      WSEvent.BATCH_IMPORT_ERROR,
      (payload: WSEventPayload[WSEvent.BATCH_IMPORT_ERROR]) => {
        batchImportStore.error = payload.error;
        batchImportStore.uploadProgress = null;
        batchImportStore.fileUploadProgress = null;
        batchImportStore.processingPending = false;
        batchImportStore.processingStarted = false;
        batchImportStore.isUploading = false;
      },
    );
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionState.value = false;
  }

  emit<E extends keyof WSEventPayload>(
    event: E,
    payload: WSEventPayload[E],
  ): void {
    if (!this.socket) {
      return;
    }

    this.socket.emit(event as string, payload);
  }

  on<E extends keyof WSEventPayload>(
    event: E,
    callback: (payload: WSEventPayload[E]) => void,
  ): void {
    const eventName = event as string;
    const wrappedCallback = callback as (...args: unknown[]) => void;

    if (!this.socket || !this.socket.connected) {
      // Socket not connected yet - queue listener for later
      this.pendingListeners.push({
        event: eventName,
        callback: wrappedCallback,
      });
      return;
    }

    // Socket.IO's generic types conflict with our custom event types
    // Safe to cast here as we control both client and server event definitions
    this.socket.on(eventName, wrappedCallback);
  }

  off<E extends keyof WSEventPayload>(event: E, callback: (payload: WSEventPayload[E]) => void): void {
    const wrapped = callback as (...args: unknown[]) => void;
    this.pendingListeners = this.pendingListeners.filter(listener =>
      listener.event !== event || listener.callback !== wrapped);
    this.socket?.off(event as string, wrapped);
  }

  get isConnected(): boolean {
    return this.connectionState.value;
  }

  getConnectionState(): Readonly<Ref<boolean>> {
    return readonly(this.connectionState);
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

export const socketService = new SocketService();
